'use strict';

const FuseJS = require('fuse.js');
const calus = require('calus');
const { DateTime } = require('luxon');

const dates = require('util/dates');

const isEmpty = require('lodash/isEmpty');
const minBy = require('lodash/minBy');
const maxBy = require('lodash/maxBy');
const isEqual = require('lodash/isEqual');
const map = require('lodash/map');
const isString = require('lodash/isString');
const some = require('lodash/some');

function controller($scope, $timeout, $location, $attrs, plannerService, translationService) {
    const vm = this;

    let allResults = [];
    let allFilteredResults = [];
    let calendar = null;
    let fuse = null;
    let previousView = false;

    vm.loading = true;
    vm.updating = true;
    vm.errorLoading = false;

    vm.totalCount = 0;
    vm.noPerformances = false;
    vm.performanceList = false;
    vm.filteredResults = [];
    vm.groupedFacets = [];
    vm.keyword = '';
    vm.activeSort = 'all';

    vm.init = function () {
        if ('performanceList' in $attrs) {
            vm.performanceList = true;
        }

        plannerService.getPerformances().then(performances => {
            // Keep track of all the results
            if ('performanceList' in $attrs) {
                allResults = performances.filter(performance => {
                    return parseInt(performance.prod_season_no, 10) === parseInt($attrs.productionNo || 0, 10);
                });
            } else {
                allResults = performances.filter(perf => {
                    return perf.show_in_listing || perf.show_in_filter
                });
            }

            if (!allResults.length) {
                vm.noPerformances = true;
            }

            // Setup fuse.js for filtering
            fuse = new FuseJS(allResults, plannerService.fuseOptions);

            // Setup calendar
            setupCalendar();

            // Do we need specific filters?
            let filters = null;

            if (vm.performanceList) {
                filters = [
                    { category: 'Period', title: translationService.translate('filter_time_of_day'), canClear: false },
                    { category: 'Price', title: translationService.translate('filter_price'), canClear: false },
                    { category: 'Access Performance', title: translationService.translate('filter_accessibility'), canClear: false }
                ];
            }

            // With the results, also generate filters
            vm.groupedFacets = plannerService.generateFacets(allResults, filters);

            // Pre-tick facets set in query string
            vm.keyword = plannerService.applyQueryString(vm.groupedFacets).keyword;

            // Get initial display going, but don't update the location
            filterResults(false);

            // Resizing changes calendar...
            window.addEventListener('resize', processResize);

            // Get a watcher going on keyword input & filters
            $scope.$watch(() => [vm.keyword, vm.groupedFacets], processChanges, true);

            // Get a watcher going on location search
            $scope.$watch(() => location.search, processLocationChange);
        }, e => {
            console.log(e);

            vm.errorLoading = true;
        }).catch( e => {
            console.log(e);

            vm.errorLoading = true;
        }).finally(function () {
            vm.loading = false;

            const queryParams = $location.search();

            if (isEmpty(queryParams) === false) {
                if ('date' in queryParams) {
                    scrollToElement('.performance-' + queryParams.date);
                } else {
                    scrollToElement('.events-nav__inner');
                }
            }
        });
    }

    vm.clear = clear;

    vm.clearAll = function () {
        clear();
    }

    vm.canClearAll = function () {
        return some(vm.groupedFacets, filter => {
            return some(filter.keywords, keyword => {
                return keyword.checked === true;
            });
        });
    }

    function clear(category) {
        vm.groupedFacets = map(vm.groupedFacets, filter => {
            if (category && filter.category !== category) {
                return filter;
            }

            filter.keywords = map(filter.keywords, keyword => {
                keyword.checked = false;

                return keyword;
            });

            return filter;
        });
    }

    function isDesktopView() {
        return window.matchMedia('(min-width: 50em)').matches === true;
    }

    function scrollToElement(selector) {
        $timeout(() => {
            const element = document.querySelector(selector);

            if (!element) {
                return;
            }

            window.scrollTo(0, element.offsetTop - (isDesktopView() ? 80 : 154 + 60 + 38));
        });
    }

    function setupCalendar() {
        const el = document.querySelector('.calus');
        if (!el) {
            return;
        }
        const config = {
            el: el,
            displayInColumn: !isDesktopView(),
            timezone: 'Europe/London',
            limitCalendar: true,
            onSelect: date => {
                if (!isDesktopView()) {
                    document.querySelector('[data-toggle="global-dates"]').click();
                }

                scrollToElement( '.performance-' + date.time.toISODate() );
            },
        }

        // Gab the minimum and maximum ranges
        if (allResults.length) {
            config.start = DateTime.fromISO(
                minBy(allResults, 'start_date').start_date_moment.clone().startOf('month').toISOString()
            );

            config.end = DateTime.fromISO(
                maxBy(allResults, 'end_date').end_date_moment.clone().endOf('month').toISOString()
            );
        }

        calendar = new calus(config);
    }

    function processResize() {
        const newView =  isDesktopView();

        if (previousView === newView) {
            return;
        }

        calendar && (calendar.displayInColumn = !newView);

        previousView = newView;
    }

    function processChanges(newValue, oldValue) {
        // Lets not do anything whilst we're loading, updating, or if
        // nothing has changed, otherwise filter function will be called
        // multiple times.
        if (vm.loading === true || vm.updating === true || isEqual(newValue, oldValue) === true) {
            return false;
        }

        // Filter results if none of the above, but also update location
        filterResults(true);
    }

    function processLocationChange(newValue, oldValue) {
        // Lets not do anything whilst we're loading, updating, or if
        // nothing has changed, otherwise filter function will be called
        // multiple times.
        if (vm.loading === true || vm.updating === true || isEqual(newValue, oldValue) === true) {
            return false;
        }

        // We only need to update the facets because items will update by
        // themselves because we're watching the facets for changes already
        vm.keyword = plannerService.applyQueryString(vm.groupedFacets).keyword;
    }

    function filterResults(updateLocation) {
        // Start updating
        vm.updating = true;

        // Start with full result list
        let filteredResults = allResults;

        // Apply keyword filtering if possible?
        if (isString(vm.keyword) && vm.keyword.length) {
            filteredResults = fuse.search(vm.keyword);
        }

        // Call the filter and update function with all the items and our
        // group of facets. It's worth noting that we could pass filtered
        // results too but the way passing by reference in this language
        // work will mean the items would not get updated here. Silly.
        vm.filteredResults = plannerService.filterAndUpdate(
            // Will not mutate, we'll do our processing using a copy of it
            filteredResults,
            // Will mutate, because we're not actually updating the object
            // itself but the values within it. JS is a silly language.
            vm.groupedFacets,
            // Forward the update location flag
            updateLocation
        );

        // We need to also call keyword update because its now a separate call
        plannerService.updateQueryStringKeyword(vm.keyword);

        // Get filtered count before we group
        vm.totalCount = vm.filteredResults.length;

        // Re-set the available dates
        calendar && calendar.setAvailable(vm.filteredResults.map(performance => {
            return DateTime.fromISO(
                performance.start_date_moment.toISOString()
            );
        }));

        // Group!
        vm.filteredResults = plannerService.groupByDay(vm.filteredResults);
        allFilteredResults = vm.filteredResults;

        // Done updating
        $timeout(() => {
            vm.updating = false;
        });
    }

    vm.sort = function (sort) {
        vm.updating = true;
        vm.activeSort = sort;

        if (sort === 'all') {
            vm.filteredResults = allFilteredResults;
        } else {
            let performances = [];

            // Build boilerplate object matching original format but only include 1 performance per object
            allFilteredResults.forEach(function(day) {
                return day.items.map(function(item) {
                  performances.push({
                    filtered: true,
                    title: day.title,
                    slug: day.slug,
                    sort: day.sort,
                    items: [item],
                  });
                });
              });

            // Sort by max price descending
            if (sort === 'best') {
                performances.sort(function(a, b) {
                    let maxPriceA = Math.max.apply(Math, a.items.map(function(item) { return item.max_price; }));
                    let maxPriceB = Math.max.apply(Math, b.items.map(function(item) { return item.max_price; }));

                    if (maxPriceA !== maxPriceB) {
                        return maxPriceB - maxPriceA; // Primary sort by max price
                    } else {
                        // Secondary sort by max_price_availability if max prices are the same
                        let maxPriceAvailabilityA = Math.max.apply(Math, a.items.map(function(item) { return item.max_price_availability; }));
                        let maxPriceAvailabilityB = Math.max.apply(Math, b.items.map(function(item) { return item.max_price_availability; }));
                        return maxPriceAvailabilityB - maxPriceAvailabilityA;
                    }
                });
            }

            // Sort by min price ascending
            if (sort === 'cheapest') {
                performances.sort(function(a, b) {
                    var minPriceA = Math.min.apply(Math, a.items.map(function(item) { return item.min_price; }));
                    var minPriceB = Math.min.apply(Math, b.items.map(function(item) { return item.min_price; }));
                    return minPriceA - minPriceB;
                });
            }

            vm.filteredResults = performances;
        }

        $timeout(() => {
            vm.updating = false;
        });
    }

    return vm;
}

module.exports = [
    '$scope', '$timeout', '$location', '$attrs', 'plannerService', 'translationService', controller
]



// WEBPACK FOOTER //
// ./widgets/controllers/Calendar.js